import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useOutlet } from "@/contexts/OutletContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  LayoutDashboard,
  ShoppingCart,
  Utensils,
  LayoutGrid,
  Users,
  Package,
  BarChart3,
  Receipt,
  Building2,
  UserCog,
  LogOut,
  ChefHat,
  Truck,
  ShoppingBasket,
  BookOpen,
  Boxes,
  FileText,
  Menu as MenuIcon,
  X,
  Settings as SettingsIcon,
  PieChart,
  Store,
} from "lucide-react";

const navSections = [
  {
    title: null,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: null, end: true },
    ],
  },
  {
    title: "Sales",
    items: [
      { to: "/pos", label: "POS Billing", icon: ShoppingCart, roles: ["cashier", "waiter", "manager"] },
      { to: "/tables", label: "Tables", icon: LayoutGrid, roles: ["cashier", "waiter", "manager"] },
      { to: "/kot", label: "KOT / Kitchen", icon: ChefHat, roles: ["kitchen", "manager"] },
      { to: "/orders", label: "Orders", icon: Receipt, roles: null },
      { to: "/customers", label: "Customers", icon: Users, roles: null },
    ],
  },
  {
    title: "Inventory & Purchase",
    items: [
      { to: "/menu", label: "Menu", icon: Utensils, roles: ["manager"] },
      { to: "/inventory", label: "Inventory", icon: Package, roles: ["manager"] },
      { to: "/purchases", label: "Purchases", icon: ShoppingBasket, roles: ["manager"] },
      { to: "/suppliers", label: "Suppliers", icon: Truck, roles: ["manager"] },
      { to: "/supplier-ledger", label: "Supplier Ledger", icon: BookOpen, roles: ["manager"] },
    ],
  },
  {
    title: "Reports",
    items: [
      { to: "/reports", label: "Sales Reports", icon: BarChart3, roles: ["manager"] },
      { to: "/menu-report", label: "Menu Report", icon: PieChart, roles: ["manager"] },
      { to: "/stock-report", label: "Stock Report", icon: Boxes, roles: ["manager"] },
      { to: "/gst-returns", label: "GST Returns", icon: FileText, roles: ["manager"] },
    ],
  },
  {
    title: "Setup",
    items: [
      { to: "/outlets", label: "Outlets", icon: Building2, roles: [] },
      { to: "/users", label: "Staff & Roles", icon: UserCog, roles: [] },
      { to: "/settings", label: "Settings", icon: SettingsIcon, roles: [] },
    ],
  },
];

export default function Layout() {
  const { user, logout, hasRole } = useAuth();
  const { outlets, outletId, select, isAdmin } = useOutlet();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const visibleSections = navSections
    .map((sec) => ({
      ...sec,
      items: sec.items.filter((n) => {
        if (n.roles === null) return true;
        if (n.roles.length === 0) return user?.role === "admin";
        return hasRole(...n.roles);
      }),
    }))
    .filter((sec) => sec.items.length > 0);

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  const onSelectOutlet = (v) => {
    select(v === "__all__" ? "" : v);
  };

  const currentOutletLabel = !outletId ? "All Outlets" : (outlets.find((o) => o.id === outletId)?.name || "Select outlet");

  return (
    <div className="min-h-screen md:flex bg-[#F9F8F6]">
      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 bg-white border-b border-[#EBE8E2] h-14 flex items-center justify-between px-3">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 rounded-md hover:bg-[#F4F2EE]"
          data-testid="mobile-menu-btn"
          aria-label="Open menu"
        >
          <MenuIcon size={20} />
        </button>
        <div className="flex items-center gap-2 min-w-0 flex-1 justify-center">
          <div className="w-7 h-7 rounded-md bg-[#D95D39] flex items-center justify-center text-white">
            <Utensils size={14} strokeWidth={2} />
          </div>
          <div className="flex flex-col items-start leading-tight min-w-0">
            <span className="font-heading font-extrabold text-[#2D2C2A] text-sm">Thali POS</span>
            <span className="text-[10px] text-[#7A7873] truncate max-w-[120px]">{currentOutletLabel}</span>
          </div>
        </div>
        <div className="w-9" />
      </header>

      {/* Backdrop on mobile */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      <aside
        className={`bg-white border-r border-[#EBE8E2] flex flex-col w-64 md:w-60 fixed md:sticky top-0 inset-y-0 left-0 z-50 h-screen transition-transform duration-300 md:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="p-4 border-b border-[#EBE8E2] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-[#D95D39] flex items-center justify-center text-white">
              <Utensils size={18} strokeWidth={2} />
            </div>
            <div>
              <div className="font-heading font-extrabold text-[#2D2C2A] leading-none">Thali POS</div>
              <div className="text-xs text-[#7A7873] mt-0.5">Restaurant Suite</div>
            </div>
          </div>
          <button
            className="md:hidden p-1.5 rounded-md hover:bg-[#F4F2EE]"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            data-testid="sidebar-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Outlet switcher */}
        <div className="p-3 border-b border-[#EBE8E2]">
          <div className="text-[10px] uppercase tracking-wider text-[#7A7873] font-semibold mb-1.5 flex items-center gap-1">
            <Store size={11} /> Active Outlet
          </div>
          <Select
            value={outletId || "__all__"}
            onValueChange={onSelectOutlet}
            disabled={!isAdmin && outlets.length <= 1}
          >
            <SelectTrigger className="h-9 border-[#EBE8E2] text-sm" data-testid="outlet-switcher">
              <SelectValue placeholder="Pick outlet" />
            </SelectTrigger>
            <SelectContent>
              {isAdmin && <SelectItem value="__all__">All Outlets (consolidated)</SelectItem>}
              {outlets.map((o) => (
                <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <nav className="flex-1 p-3 space-y-3 overflow-y-auto">
          {visibleSections.map((sec, idx) => (
            <div key={sec.title || `s-${idx}`}>
              {sec.title && (
                <div className="px-2 mb-1.5 text-[10px] uppercase tracking-wider text-[#7A7873] font-semibold">
                  {sec.title}
                </div>
              )}
              <div className="space-y-1">
                {sec.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive
                          ? "bg-[#FBE9E2] text-[#D95D39] font-semibold"
                          : "text-[#2D2C2A] hover:bg-[#F4F2EE]"
                      }`
                    }
                  >
                    <item.icon size={17} strokeWidth={1.75} />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-[#EBE8E2]">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-semibold text-[#2D2C2A] truncate" data-testid="current-user-name">
              {user?.name || "—"}
            </div>
            <div className="text-xs text-[#7A7873] capitalize">{user?.role}</div>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#2D2C2A] hover:bg-[#F4F2EE] transition-colors"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
